/**
 * Types for AI word list generation feature.
 *
 * Defines request types for the generation endpoint.
 */

/**
 * Request payload for generating a word list from a prompt.
 * Sent to POST /api/admin/word-lists/generate
 */
export interface WordListGenerateRequest {
  /** Text description of the desired word list (e.g., "50 common kitchen vocabulary") */
  prompt: string;
  /** Target language code for the words (e.g., 'en', 'de') */
  language_code: string;
}

/**
 * Request payload for extending an existing word list with more words.
 * Sent to POST /api/admin/word-lists/{id}/extend
 */
export interface ExtendWordListRequest {
  /** Text description of additional words to add (e.g., "add 10 more cooking verbs") */
  prompt: string;
}

/**
 * Response from CSV word import.
 * Returned from POST /api/admin/word-lists/{id}/import-words
 */
export interface ImportWordsFromCsvResponse {
  total_in_file: number;
  skipped_already_in_list: number;
  added_existing: number;
  created_new: number;
  failed: number;
  failed_words: string[];
}

// =============================================================================
// Two-Phase Generation Types
// =============================================================================

/**
 * Word list category types
 */
export type WordListCategoryType = 'topics' | 'exams' | 'vocabulary';

/**
 * Request payload for generating word list ideas (Step 1).
 * Sent to POST /api/admin/word-lists/generate-ideas
 */
export interface GenerateIdeasRequest {
  /** Category type: topics, exams, or vocabulary */
  type: WordListCategoryType;
  /** Target language code for the words (e.g., 'en', 'de') */
  language_code: string;
  /** Additional context or constraints for idea generation */
  prompt?: string;
  /** Number of ideas to generate (default: 5, max: 20) */
  count?: number;
}

/**
 * A single word list idea generated by the LLM.
 */
export interface WordListIdea {
  /** Short, descriptive name (2-4 words) */
  name: string;
  /** One sentence describing what learners will gain */
  description: string;
  /** Difficulty level */
  difficulty: 'Beginner' | 'Elementary' | 'Intermediate' | 'Upper-Intermediate' | 'Advanced';
  /** Recommended number of words (30-100) */
  estimatedWordCount: number;
  /** 3-5 example words to preview theme */
  sampleWords: string[];
  /** Learning goal IDs that this list serves (Story 15.8: now integers) */
  learningGoalIds: number[];
}

/**
 * API response for word list idea (snake_case from API)
 */
export interface WordListIdeaApiResponse {
  name: string;
  description: string;
  difficulty: string;
  estimated_word_count: number;
  sample_words: string[];
  learning_goal_ids: number[];
}

/**
 * Response from word list idea generation.
 * Returned from POST /api/admin/word-lists/generate-ideas
 */
export interface GenerateIdeasResponse {
  ideas: WordListIdeaApiResponse[];
}

/**
 * Request payload for generating a full word list from an idea (Step 2).
 * Sent to POST /api/admin/word-lists/generate-from-idea
 */
export interface GenerateFromIdeaRequest {
  /** The idea to generate a full word list from */
  idea: {
    name: string;
    description: string;
    difficulty: string;
    estimated_word_count: number;
    sample_words: string[];
    learning_goal_ids: number[];
  };
  /** Target language code for the words */
  language_code: string;
  /** Category type: topics, exams, or vocabulary */
  type: WordListCategoryType;
}

/**
 * Map API response to frontend type
 */
export function mapWordListIdeaFromApi(idea: WordListIdeaApiResponse): WordListIdea {
  return {
    name: idea.name,
    description: idea.description,
    difficulty: idea.difficulty as WordListIdea['difficulty'],
    estimatedWordCount: idea.estimated_word_count,
    sampleWords: idea.sample_words,
    learningGoalIds: idea.learning_goal_ids,
  };
}
